<template>
  <div class="claim__item claim__item--path" :class="hasTask ? 'warning' : ''" ref="task">
    <lennoc-icon :image="messageIcon" :class="iconClass" />

    <div class="messages__header" :class="additionalHeaderClass">
      <h3 :class="headerClass">{{ itemHeader }}</h3>
      <h5 v-if="isUserMessage" class="message__subtitle">{{ Document.Name || Document.Description }}</h5>
      <p class="timestamp">{{ dateTime }}</p>
    </div>

    <div class="messages__content">
      <claim-document
        v-if="documentId"
        :documentId="documentId"
        :hasTask="hasTask"
        :relatedClaimId="relatedClaimId">
      </claim-document>
      <claim-lead
        v-if="isClaimLead"
      >
      </claim-lead>
      <claim-passengers
        v-if="hasPassengers"
        :documentId="documentId">
      </claim-passengers>
      <claim-file-receipts
        v-if="isUploadRequest && renderViewReceipts"
        :documentId="documentId"
        :isClaimClosed="claim.ClaimClosed">
      </claim-file-receipts>
      <claim-file-documents
        v-if="isUploadRequest && ( renderViewDocuments || renderViewReceipts )"
        :documentId="documentId"
        :nonLegacyReceipts="claim.Receipts"
        :isClaimClosed="claim.ClaimClosed">
      </claim-file-documents>
      <claim-file-receipt-upload
        v-if="isUploadRequest && renderViewReceipts && !claim.ClaimClosed"
        :documentId="documentId">
      </claim-file-receipt-upload>
      <claim-nested-document
        v-if="!!nestedDocument"
        :Document="nestedDocument">
      </claim-nested-document>
      <claim-child-messages
       v-if="hasChildMessages"
        :rootMessage="Document" :isWarning="hasTask">
      </claim-child-messages>
      <claim-react-button
        v-if="canReact"
        :rootMessage="Document"
        :setTaskPerformed="completeTask">
      </claim-react-button>
      <claim-lead-react-button
        v-if="isConsumenten"
        :documentId="documentId"
        >
      </claim-lead-react-button>
    </div>
  </div>
</template>

<script>
import { formatDate } from '@/utils/datetime'
import ClaimDocument from '@/components/claims/ClaimDocument.vue'
import ClaimPassengers from '@/components/person/ClaimPassengers.vue'
import ClaimFileDocuments from '@/components/claims/ClaimFileDocuments.vue'
import ClaimNestedDocument from '@/components/claims/ClaimNestedDocument.vue'
import ClaimChildMessages from '@/components/claims/ClaimChildMessages.vue'
import ClaimReactButton from '@/components/claims/ClaimReactButton.vue'
import ClaimFileReceipts from '@/components/claims/ClaimFileReceipts.vue'
import ClaimFileReceiptUpload from '@/components/claims/ClaimFileReceiptUpload.vue'
import ClaimLead from '@/components/person/ClaimLead.vue'
import ClaimLeadReactButton from '@/components/claims/ClaimLeadReactButton.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'ClaimStatusTabItem',
  components: {
    ClaimDocument,
    ClaimPassengers,
    ClaimFileDocuments,
    ClaimNestedDocument,
    ClaimChildMessages,
    ClaimReactButton,
    ClaimFileReceipts,
    ClaimFileReceiptUpload,
    ClaimLead,
    ClaimLeadReactButton
  },
  data () {
    return {
      isClaimRequestHandled: false
    }
  },
  props: {
    documentId: {
      type: String,
      required: true
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$route.params.anchor && this.$route.params.anchor === this.documentAction) {
        this.$refs.task.scrollIntoView()
      }
    })
  },
  computed: {
    ...mapGetters({
      getDocumentById: 'claim/getDocumentById',
      getUiString: 'uistrings/getUiString',
      getDropzoneStrings: 'uistrings/getDropzoneStrings',
      getSupportedFileTypes: 'getSupportedFileTypes',
      messageTask: 'claim/getDocumentTask',
      childClaims: 'dossier/getChildClaims',
      claim: 'claim/currentClaim'
    }),
    Document () {
      return this.getDocumentById(this.documentId)
    },
    messageIcon () {
      if (this.documentAction === 'MessageCustomer') {
        return 'user'
      }
      if (this.documentAction === 'MessageAirline') {
        return 'plane-flat'
      }
      if (this.documentAction === 'MessageEUclaim' || !this.documentAction) {
        return 'logo-icon'
      }
      return this.hasTask ? 'warning' : 'check--large'
    },
    iconClass () {
      if (this.documentAction === 'MessageEUclaim' || !this.documentAction) {
        return 'icon--logo'
      }
      return this.messageIcon === 'check--large' ? 'icon--check' : ''
    },
    documentAction () {
      return this.Document.DocumentAction || ''
    },
    documentDescription () {
      return this.Document.Description || ''
    },
    documentCanReact () {
      if (this.Document.CanReact === null) {
        return false
      }
      return this.Document.CanReact
    },
    relatedClaimId () {
      if (this.claim.ParentClaimId) {
        return this.claim.ParentClaimId
      }
      const children = this.childClaims(this.claim.Id)
      if (children && children.length) {
        return children[0].Id
      }
      return undefined
    },
    itemHeader () {
      if (this.isUserMessage) {
        return this.getUiString(['claim_details', 'claim', 'your_message'])
      }
      if (this.isUploadRequest) {
        return this.documentDescription
      }
      if (this.isUserDocument) {
        return this.getUiString([
          'claim_details',
          'details',
          'documents',
          'added_document_title'
        ])
      } else if (!this.isUploadRequest && !this.isUploadRequest) {
        return this.documentDescription
      }
      return ''
    },
    headerClass () {
      if (this.isUploadRequest) {
        return 'messages__item--title title--l'
      }
      if (this.documentAction === 'DocumentCustomer') {
        return 'messages--title title--l title--break'
      }
      return 'messages--title title--l'
    },
    additionalHeaderClass () {
      return this.isUserMessage ? 'messages__header--user-message' : ''
    },
    hasTask () {
      return !!this.messageTask(this.documentId)
    },
    isClaimLead () {
      return this.documentAction === 'RequestApproveSubscriptionWithPartner'
    },
    isConsumenten () {
      return this.documentAction === 'RequestSubscriptionWithPartner'
    },
    isUserMessage () {
      return this.documentAction === 'MessageCustomer'
    },
    isUserDocument () {
      return this.documentAction === 'DocumentCustomer' && this.hasFileUploaded
    },
    isUploadRequest () {
      return this.documentAction === 'RequestUploadDocument' ||
        this.documentAction === 'RequestUploadReceipts'
    },
    hasPassengers () {
      return this.documentAction === 'RequestUpdatePassengerDetails'
    },
    hasVideo () {
      return (this.Document.Message || '').indexOf('[video]') === 0
    },
    hasFileUploaded () {
      return this.Document.Children.filter(c => {
        return c.DocumentAction === 'DocumentCustomer' && c.ContainsFile
      }).length > 0
    },
    nestedMessages () {
      return this.Document.Children.filter(m => !m.ContainsFile && !!m.DocumentAction)
    },
    hasChildMessages () {
      return this.nestedMessages.length > 0
    },
    nestedDocument () {
      const nestedMessages = this.Document.Children.filter(m => !m.ContainsFile && !m.DocumentAction)
      return nestedMessages[0] || null
    },
    canReact () {
      return (this.hasChildMessages && this.nestedMessages[0].CanReact) ||
      (this.nestedDocument && this.nestedDocument.CanReact) ||
      (this.Document && this.Document.CanReact) ||
      (this.documentAction === 'RequestUploadDocument' && this.hasFileUploaded && this.Document.CanReact)
    },
    completeTask () {
      return ['RequestReply', 'RequestMarkRead'].indexOf(this.documentAction) > -1
    },
    dateTime () {
      return formatDate(this.Document.TimeStamp, 'datetime')
    },
    renderViewDocuments () {
      if (this.documentAction === 'RequestUploadDocument') {
        return true
      }

      if (this.documentAction === 'RequestUploadReceipts') {
        const receiptRequest = this.claim.Documents.find(d => {
          return d.DocumentAction === 'RequestUploadDocument'
        })

        const legacyReceipt = receiptRequest.Children.find(c => {
          if (c.DocumentChecklist === null) {
            return false
          }
          return c.DocumentChecklist.ContainsReceipts
        })

        if (legacyReceipt) {
          return true
        }
      }
      return false
    },
    renderViewReceipts () {
      return this.documentAction === 'RequestUploadReceipts'
    }
  }
}
</script>
